'use client';

import dynamic from 'next/dynamic';
import Image, { StaticImageData } from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import {
  useIsMobileScreenWidthOrThinner,
  useIsTabletScreenWidthOrThinner,
} from '@hooks/useScreenWidth';
import { IBoundingBox } from '@app-types/geo';
import { Button } from '../../../Clickables/Buttons/Button';
import { IStateDataSpeciesList } from '../../../FindUSSpecies/helpers/buildStateDataSpecies';
import { Heading } from '../../../Heading/Heading';
import { MapLoadingOverlay } from '../MapLoadingOverlay/MapLoadingOverlay';
import { MapLegend } from './components/MapLegend/MapLegend';
import { calculateStateColors } from './helpers/calculateStateColors';
import { Text } from '../../../Text/Text';
import { MapSEOLinks } from './components/MapSEOLinks/MapSEOLinks';

const DynamicCountryMap = dynamic(async () => import('./CountryMap').then(mod => mod.CountryMap), {
  loading: () => <MapLoadingOverlay />,
});

interface IProps {
  bbox: IBoundingBox;
  countryId: string;
  countryName: string;
  className?: string;
  id: string;
  sourceId?: string;
  title?: string;
  subTitle?: string;
  preload?: boolean;
  placeholderClassName?: string;
  placeholderImage?: string | StaticImageData;
  topSpeciesData?: IStateDataSpeciesList[];
  speciesGroupingExternalId?: string;
}

export const CountryMapSection = ({
  bbox,
  countryId,
  countryName,
  className,
  id,
  sourceId,
  title,
  subTitle,
  preload = false,
  placeholderClassName,
  placeholderImage,
  topSpeciesData,
  speciesGroupingExternalId,
}: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const { currentPage } = useAnalytics();
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [loadMap, setLoadMap] = useState<boolean>(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<number>(0);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [triggerMapResize, setTriggerMapResize] = useState<boolean>(false);

  const stateColors = calculateStateColors({
    stateData: topSpeciesData ?? [],
    speciesGroupingExternalId: speciesGroupingExternalId,
  });

  const isMobileScreen = useIsMobileScreenWidthOrThinner();
  const isTabletScreen = useIsTabletScreenWidthOrThinner();

  const getContainerHeight = (): string => {
    if (!clientHasRendered || placeholderHeight === 0) {
      return 'auto';
    }
    if (isMobileScreen && loadMap && isExpanded) {
      return `${placeholderHeight * 2}px`;
    }
    if (isTabletScreen && loadMap && isExpanded) {
      return `${(placeholderHeight * 3) / 2}px`;
    }
    return `${placeholderHeight}px`;
  };

  useEffect(() => {
    const handleResize = (): void => {
      if (placeholderRef.current?.offsetHeight) {
        setPlaceholderHeight(placeholderRef.current.offsetHeight);
      }
    };

    if (clientHasRendered && placeholderRef.current?.offsetHeight) {
      setPlaceholderHeight(placeholderRef.current.offsetHeight);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [clientHasRendered]);

  const handleMapClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'CountryMapSection',
        source_id: sourceId,
        type: 'openMap',
        page: currentPage,
      },
    });
    setLoadMap(true);
    setIsExpanded(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        `relative w-full scroll-mt-(--navigation-scrolltop-offset-mobile) p-0 sm:scroll-mt-(--navigation-scrolltop-offset)`,
        className,
      )}
    >
      <div className="bg-lake-fog w-full sm:rounded-2xl">
        <div className="md:w-col-8 flex w-full flex-col items-center gap-8 px-2 py-8 sm:gap-11 sm:px-14 md:mx-auto">
          {title && (
            <div className="flex flex-col gap-2 px-4 sm:px-0">
              <Heading variant="md" as="h2" className="m-0 sm:text-center">
                {title}
              </Heading>
              {subTitle && (
                <Text variant="md" className="m-0 sm:text-center">
                  {subTitle}
                </Text>
              )}
            </div>
          )}
          <div
            className="w-full transition-[height]"
            style={{
              height: getContainerHeight(),
            }}
            onTransitionEnd={e => {
              if (e.propertyName === 'height') {
                setTriggerMapResize(true);
              }
            }}
          >
            <div className={`width-full ${loadMap ? 'h-full' : 'h-auto'} md:rounded-2xl`}>
              <div className="relative flex size-full items-center justify-center bg-cover">
                {topSpeciesData && (
                  <MapSEOLinks
                    countryId={countryId}
                    countryName={countryName}
                    mode={speciesGroupingExternalId ? 'speciesGroupings' : 'states'}
                    topSpeciesData={topSpeciesData}
                    testId="country-map-links"
                    speciesGroupingExternalId={speciesGroupingExternalId}
                  />
                )}
                {/* Dynamically loaded Map */}
                {loadMap && (
                  <DynamicCountryMap
                    bbox={bbox}
                    countryId={countryId}
                    countryName={countryName}
                    stateColors={stateColors}
                    triggerMapResize={triggerMapResize}
                    setTriggerMapResize={setTriggerMapResize}
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    speciesGroupingExternalId={speciesGroupingExternalId}
                  />
                )}
                {/* Placeholder Image */}
                <div className="absolute inset-0 overflow-hidden">
                  {placeholderImage && (
                    <Image
                      fill
                      preload={preload}
                      alt="Map"
                      src={placeholderImage}
                      className="size-full object-contain"
                      sizes="(min-width: 1680px) 1552px, 100vw"
                    />
                  )}
                </div>
                {/* Placeholder Text */}
                <div ref={placeholderRef} className="z-mid flex w-full items-center justify-center">
                  <div
                    className={twMerge(
                      `${loadMap ? 'opacity-0' : 'opacity-100'} mx-8 my-7 flex h-32 w-full flex-col items-center justify-center gap-4 px-6 py-6 sm:mx-28 sm:my-16 sm:h-48 sm:py-8 md:my-24 md:h-56 md:gap-6`,
                      placeholderClassName,
                    )}
                  >
                    <div className={`flex flex-col items-center justify-center`}>
                      <Button
                        size="sm"
                        className={`w-[129px] px-1.5 py-[9px] text-sm md:w-[213px] md:p-3 md:text-base`}
                        onClick={handleMapClick}
                      >
                        Explore map
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {loadMap && <MapLegend />}
        </div>
      </div>
    </div>
  );
};
